import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { BE_API_AUTH_URL } from '../constants/app.constants';

const BACKEND_ORIGIN = BE_API_AUTH_URL.replace(/\/api\/auth.*$/, '');
import {
  CatalogResponse,
  CreateSignOrderDraftRequest,
  CreateOrderRequest,
  OrderDetailResponse,
  OrderListResponse,
  PosterPreviewRequest,
  PosterPreviewResponse,
  PropertySearchResponse,
  QuoteRequest,
  QuoteResponse,
  SelectAllResponse,
  SignOrderDraft,
  SignOrderDraftListResponse,
  SignOrderDraftResumeResponse,
  UpdateSignOrderDraftRequest,
} from '../models/sign-order.models';

@Injectable({
  providedIn: 'root',
})
export class SignOrderService {
  private readonly base = `${BE_API_AUTH_URL}/sign-orders`;

  constructor(private http: HttpClient) {}

  getCatalog(): Observable<CatalogResponse> {
    return this.http.get<CatalogResponse>(`${this.base}/catalog`);
  }

  searchProperties(search: string, page: number, pageSize: number, offset?: number): Observable<PropertySearchResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (search) {
      params = params.set('search', search);
    }
    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }
    return this.http.get<PropertySearchResponse>(`${this.base}/properties`, { params });
  }

  selectAll(search: string): Observable<SelectAllResponse> {
    return this.http.post<SelectAllResponse>(`${this.base}/selections/select-all`, { search });
  }

  clearSelection(selectionId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/selections/${selectionId}`);
  }

  searchSelectionProperties(selectionId: string, search: string, page: number, pageSize: number): Observable<PropertySearchResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<PropertySearchResponse>(`${this.base}/selections/${selectionId}/properties`, { params });
  }

  requestQuote(request: QuoteRequest): Observable<QuoteResponse> {
    return this.http.post<QuoteResponse>(`${this.base}/quote`, request);
  }

  createOrder(request: CreateOrderRequest): Observable<OrderDetailResponse> {
    return this.http.post<OrderDetailResponse>(`${this.base}`, request);
  }

  createDraft(request: CreateSignOrderDraftRequest): Observable<SignOrderDraft> {
    return this.http.post<SignOrderDraft>(`${this.base}/drafts`, request);
  }

  listDrafts(page: number, pageSize: number): Observable<SignOrderDraftListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<SignOrderDraftListResponse>(`${this.base}/drafts`, { params });
  }

  getDraft(publicId: string): Observable<SignOrderDraftResumeResponse> {
    return this.http.get<SignOrderDraftResumeResponse>(`${this.base}/drafts/${encodeURIComponent(publicId)}`);
  }

  updateDraft(publicId: string, request: UpdateSignOrderDraftRequest): Observable<SignOrderDraft> {
    return this.http.put<SignOrderDraft>(`${this.base}/drafts/${encodeURIComponent(publicId)}`, request);
  }

  deleteDraft(publicId: string, revision: number): Observable<void> {
    const params = new HttpParams().set('revision', revision.toString());
    return this.http.delete<void>(`${this.base}/drafts/${encodeURIComponent(publicId)}`, { params });
  }

  listOrders(page: number, pageSize: number): Observable<OrderListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<OrderListResponse>(`${this.base}`, { params });
  }

  getOrder(publicId: string): Observable<OrderDetailResponse> {
    return this.http.get<OrderDetailResponse>(`${this.base}/${publicId}`);
  }

  loadImageBlob(imageUrl: string): Observable<string> {
    const absoluteUrl = imageUrl.startsWith('http') ? imageUrl : BACKEND_ORIGIN + imageUrl;
    return this.http.get(absoluteUrl, { responseType: 'blob' }).pipe(
      map((blob) => URL.createObjectURL(blob)),
    );
  }

  postPosterPreview(payload: PosterPreviewRequest): Observable<PosterPreviewResponse> {
    return this.http.post<PosterPreviewResponse>(`${this.base}/poster-preview`, payload);
  }
}
